import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext.jsx';
import { apiClient } from '../../../lib/apiClient.js';

const APR = 0.085;

const PERIOD_OPTIONS = [1, 2, 3, 4, 5, 7, 10, 15];

const EXAMPLE_QUERIES = [
  "I want to take a loan of $10,000 for 5 years",
  "Can I borrow $25,000 for a primary home purchase over 10 years?",
  "What is the maximum loan I can take?",
  "I'd like a $15,000 loan repaid over 3 years for general purposes",
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n, decimals = 0) {
  return Number(n).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: decimals,
    minimumFractionDigits: decimals,
  });
}

function formatDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function calcMonthly(principal, years) {
  const P = Number(principal) || 0;
  if (P <= 0 || years <= 0) return 0;
  const r = APR / 12;
  const n = years * 12;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function buildSchedule(principal, years) {
  const P = Number(principal) || 0;
  if (P <= 0 || years <= 0) return [];
  const r = APR / 12;
  const n = years * 12;
  const M = (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
  const rows = [];
  let balance = P;
  for (let i = 1; i <= n; i++) {
    const interest = balance * r;
    const principal_paid = M - interest;
    balance = Math.max(0, balance - principal_paid);
    rows.push({ month: i, payment: M, principal: principal_paid, interest, balance });
  }
  return rows;
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white flex-shrink-0">
      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
    </svg>
  );
}

function ChevronDown() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}

function ChevronUp() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5">
      <path d="M18 15l-6-6-6 6" />
    </svg>
  );
}

// ── Skeleton ──────────────────────────────────────────────────────────────────

function MetricsSkeleton() {
  return (
    <div className="grid grid-cols-3 gap-3">
      {[0, 1, 2].map((i) => (
        <div key={i} className="card p-4 space-y-2 animate-pulse">
          <div className="h-2 w-14 bg-bg-s3 rounded" />
          <div className="h-7 w-20 bg-bg-s3 rounded" />
          <div className="h-2 w-16 bg-bg-s3 rounded" />
        </div>
      ))}
    </div>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────────

function MetricCard({ label, value, subtext, valueClass }) {
  return (
    <div className="card p-4">
      <p className="text-[10px] font-mono uppercase tracking-widest text-text-faint mb-1">{label}</p>
      <p className={`text-[22px] font-bold tabular-nums leading-tight ${valueClass || 'text-text'}`}>{value}</p>
      {subtext && <p className="text-[11px] text-text-faint mt-0.5">{subtext}</p>}
    </div>
  );
}

// ── Loan status badge ─────────────────────────────────────────────────────────

function LoanBadge({ status }) {
  if (status === 'active') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-success/10 text-success border border-success/20">
        Active
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-warning/10 text-warning border border-warning/20">
        Pending
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-bg-s3 text-text-faint border border-border capitalize">
      {status || 'Unknown'}
    </span>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function LoanPage() {
  const { principal } = useAuth();
  const navigate = useNavigate();

  // Data
  const [participant, setParticipant] = useState(null);
  const [loanActivities, setLoanActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Calculator
  const [calcAmount, setCalcAmount] = useState('10000');
  const [calcYears, setCalcYears] = useState(5);
  const [showSchedule, setShowSchedule] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  // Query / send-to-chat
  const [selectedQuery, setSelectedQuery] = useState('');
  const [customQuery, setCustomQuery] = useState('');

  // ── Fetch ──────────────────────────────────────────────────────────────────

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [participantsRes, activityRes] = await Promise.all([
        apiClient.listParticipants(),
        apiClient.getParticipantActivity(),
      ]);

      const list = participantsRes?.participants ?? (Array.isArray(participantsRes) ? participantsRes : []);
      const found = list.find((p) => p.participant_id === principal?.participantId) ?? null;
      setParticipant(found);

      const all = activityRes?.activities ?? [];
      // Loans appear in two forms:
      //   type:"loan"     → from participant_loans (pre-seeded / recordkeeper, has outstanding + loan_status)
      //   type:"executed" + action:"loan_initiation" → from transactions table (taken through chat)
      // The activity endpoint deduplicates by date, keeping the executed record when both exist,
      // so we must catch both forms here.
      setLoanActivities(
        all.filter((a) => a.type === 'loan' || (a.type === 'executed' && a.action === 'loan_initiation'))
      );
    } catch (err) {
      setError(err.message || 'Failed to load loan data');
    } finally {
      setIsLoading(false);
    }
  }, [principal?.participantId]);

  useEffect(() => { load(); }, [load]);

  // ── Derived metrics ────────────────────────────────────────────────────────

  // A record is an active loan if: status is explicitly "active", or there is no status
  // (executed records from the transactions table have no loan_status — assume active).
  // Exclude explicitly closed/paid records.
  const activeLoans = loanActivities.filter(
    (a) => !a.loan_status || a.loan_status === 'active',
  );
  const activeLoanCount = activeLoans.length;
  // outstanding is set for participant_loans records; fall back to the original amount
  // for executed records where the balance hasn't been separately tracked yet.
  const totalOutstanding = activeLoans.reduce(
    (sum, a) => sum + (Number(a.outstanding) || Number(a.amount) || 0),
    0,
  );
  const loanHeadroom = participant?.loan_headroom ?? null;

  // ── Calculator ─────────────────────────────────────────────────────────────

  const P = parseFloat(String(calcAmount).replace(/,/g, '')) || 0;
  const M = useMemo(() => calcMonthly(P, calcYears), [P, calcYears]);
  const totalRepaid = M * calcYears * 12;
  const totalInterest = totalRepaid - P;
  const schedule = useMemo(() => buildSchedule(P, calcYears), [P, calcYears]);
  const visibleRows = showAllRows ? schedule : schedule.slice(0, 12);

  // ── Query handlers ─────────────────────────────────────────────────────────

  function selectExample(q) {
    setSelectedQuery(q);
    setCustomQuery(q);
  }

  function handleTextarea(e) {
    setCustomQuery(e.target.value);
    setSelectedQuery('');
  }

  const activeQuery = customQuery.trim();

  function sendToChat() {
    if (!activeQuery) return;
    navigate('/participant/chat', { state: { chatDraft: activeQuery } });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="max-w-2xl mx-auto space-y-5">

      {/* Hero card with left accent bar */}
      <div className="card overflow-hidden flex">
        <div className="w-1 bg-accent flex-shrink-0" />
        <div className="p-5 flex-1">
          <h1 className="text-[20px] font-bold text-text leading-tight">Plan Loan</h1>
          <p className="text-sm text-text-muted mt-1 leading-relaxed">
            Borrow against your vested 401(k) balance. Loans are subject to the lesser of
            $50,000 or 50% of your vested balance (IRC §72(p)) and repaid via payroll deduction.
            All requests run through FAP compliance automatically.
          </p>
        </div>
      </div>

      {/* Blackout banner */}
      {!isLoading && participant?.blackout_active && (
        <div className="card p-4 border-warning/40 bg-warning/5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-full bg-warning/15 text-warning flex items-center justify-center text-sm font-bold flex-shrink-0">!</div>
          <div>
            <p className="text-[13px] font-semibold text-warning">Plan blackout in effect</p>
            <p className="text-[12px] text-text-muted mt-0.5">
              All account writes are temporarily suspended during this blackout period per ERISA §101(i).
              You may still view your loan status and use the calculator, but new requests cannot be submitted until the blackout ends.
            </p>
          </div>
        </div>
      )}

      {/* ── Status section ──────────────────────────────────────────────────── */}
      <section className="space-y-3">
        <h2 className="text-[11px] font-mono uppercase tracking-widest text-text-faint">Your Loan Status</h2>

        {isLoading ? (
          <MetricsSkeleton />
        ) : (
          <div className="grid grid-cols-3 gap-3">
            <MetricCard
              label="Active Loans"
              value={activeLoanCount}
              subtext={activeLoanCount === 0 ? 'No active loans' : `${activeLoanCount} loan${activeLoanCount !== 1 ? 's' : ''}`}
              valueClass={activeLoanCount === 0 ? 'text-success' : 'text-warning'}
            />
            <MetricCard
              label="Total Outstanding"
              value={totalOutstanding > 0 ? fmt(totalOutstanding) : '$0'}
              subtext="Principal remaining"
              valueClass={totalOutstanding > 0 ? 'text-warning' : 'text-text'}
            />
            <MetricCard
              label="Available Headroom"
              value={loanHeadroom != null ? fmt(loanHeadroom) : '—'}
              subtext="Max new loan amount"
              valueClass="text-text"
            />
          </div>
        )}

        {/* Fetch error — soft, calculator and guide still render below */}
        {!isLoading && error && (
          <div className="card p-3 border-warning/30 bg-warning/5">
            <p className="text-xs text-warning">
              {error} — account data unavailable; the calculator and request guide still work.
            </p>
          </div>
        )}

        {/* Active loans table */}
        {!isLoading && activeLoans.length > 0 && (
          <div className="card overflow-hidden">
            <div className="px-4 pt-4 pb-2 border-b border-border">
              <p className="text-[11px] font-mono uppercase tracking-wider text-text-faint">Active Loans</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-bg-s2">
                    <th className="text-left text-[10px] font-mono text-text-faint uppercase tracking-wide px-4 py-2 font-normal">
                      Date
                    </th>
                    <th className="text-right text-[10px] font-mono text-text-faint uppercase tracking-wide px-4 py-2 font-normal">
                      Amount
                    </th>
                    <th className="text-right text-[10px] font-mono text-text-faint uppercase tracking-wide px-4 py-2 font-normal">
                      Outstanding Balance
                    </th>
                    <th className="text-left text-[10px] font-mono text-text-faint uppercase tracking-wide px-4 py-2 font-normal">
                      Term
                    </th>
                    <th className="text-left text-[10px] font-mono text-text-faint uppercase tracking-wide px-4 py-2 font-normal">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {activeLoans.map((loan, idx) => {
                    const amount = Number(loan.amount) || 0;
                    const outstanding = loan.outstanding != null ? Number(loan.outstanding) : amount;
                    const paidFrac = amount > 0 ? Math.max(0, Math.min(1, (amount - outstanding) / amount)) : 0;
                    const termYears = loan.maturity_date && loan.timestamp
                      ? Math.round((new Date(loan.maturity_date) - new Date(loan.timestamp)) / (365.25 * 24 * 3600 * 1000))
                      : null;
                    return (
                      <tr
                        key={loan.id || idx}
                        className="border-b border-border last:border-0 hover:bg-bg-s2 transition-colors"
                      >
                        <td className="px-4 py-3 text-text-muted tabular-nums text-[13px]">
                          {formatDate(loan.timestamp)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums font-medium text-text text-[13px]">
                          {amount ? fmt(amount) : '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="tabular-nums text-text text-[13px]">
                            {outstanding != null ? fmt(outstanding) : '—'}
                          </span>
                          {amount > 0 && (
                            <div className="mt-1.5 w-24 ml-auto h-1 bg-bg-s3 rounded-full overflow-hidden">
                              <div
                                className="h-full bg-success rounded-full transition-all duration-300"
                                style={{ width: `${paidFrac * 100}%` }}
                              />
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-[13px] text-text-muted tabular-nums whitespace-nowrap">
                          {termYears != null ? `${termYears} yr${termYears !== 1 ? 's' : ''}` : '—'}
                        </td>
                        <td className="px-4 py-3">
                          <LoanBadge status={loan.loan_status || 'active'} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>

      {/* ── Amortization Calculator ──────────────────────────────────────────── */}
      <div className="card overflow-hidden">
        <div className="px-5 pt-5 pb-4 border-b border-border">
          <h2 className="text-[15px] font-semibold text-text">Amortization Calculator</h2>
          <p className="text-[12px] text-text-muted mt-0.5">
            Fixed APR 8.5% (IRS prime-rate safe harbor). For illustration only — not a loan offer.
          </p>
        </div>

        <div className="p-5 bg-bg-s2 space-y-5">

          {/* Inputs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="label-mono">Loan Amount ($)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm select-none">$</span>
                <input
                  type="number"
                  min={500}
                  max={50000}
                  step={500}
                  value={calcAmount}
                  onChange={(e) => {
                    setCalcAmount(e.target.value);
                    setShowAllRows(false);
                  }}
                  className="input-field pl-7 tabular-nums"
                />
              </div>
            </div>
            <div className="flex-1">
              <label className="label-mono">Repayment Period</label>
              <div className="flex flex-wrap gap-1.5">
                {PERIOD_OPTIONS.map((yr) => (
                  <button
                    key={yr}
                    type="button"
                    onClick={() => {
                      setCalcYears(yr);
                      setShowAllRows(false);
                    }}
                    className={[
                      'px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors duration-150',
                      calcYears === yr
                        ? 'bg-accent text-white border-accent shadow-sm'
                        : 'bg-bg-surface text-text-muted border-border-strong hover:border-accent/40 hover:text-text',
                    ].join(' ')}
                  >
                    {yr}y
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Summary chips */}
          {P > 0 && M > 0 && (
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-bg-surface border border-border p-3 text-center">
                <p className="text-[10px] font-mono uppercase tracking-wide text-text-faint mb-1">Monthly Payment</p>
                <p className="text-[18px] font-bold text-accent tabular-nums">{fmt(M, 2)}</p>
              </div>
              <div className="rounded-xl bg-bg-surface border border-border p-3 text-center">
                <p className="text-[10px] font-mono uppercase tracking-wide text-text-faint mb-1">Total Repaid</p>
                <p className="text-[18px] font-bold text-text tabular-nums">{fmt(totalRepaid)}</p>
              </div>
              <div className="rounded-xl bg-bg-surface border border-border p-3 text-center">
                <p className="text-[10px] font-mono uppercase tracking-wide text-text-faint mb-1">Total Interest</p>
                <p className="text-[18px] font-bold text-warning tabular-nums">{fmt(totalInterest)}</p>
              </div>
            </div>
          )}

          {/* Amortization schedule toggle */}
          {P > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setShowSchedule((s) => !s)}
                className="flex items-center gap-1.5 text-[12px] font-medium text-accent hover:text-accent-dark transition-colors"
              >
                {showSchedule ? <ChevronUp /> : <ChevronDown />}
                {showSchedule ? 'Hide' : 'Show'} amortization schedule
              </button>

              {showSchedule && schedule.length > 0 && (
                <div className="mt-3 rounded-lg border border-border overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px]" style={{ fontVariantNumeric: 'tabular-nums' }}>
                      <thead>
                        <tr className="bg-bg-s3 border-b border-border">
                          <th className="text-right font-mono font-normal text-text-faint px-3 py-2">Month</th>
                          <th className="text-right font-mono font-normal text-text-faint px-3 py-2">Payment</th>
                          <th className="text-right font-mono font-normal text-text-faint px-3 py-2">Principal</th>
                          <th className="text-right font-mono font-normal text-text-faint px-3 py-2">Interest</th>
                          <th className="text-right font-mono font-normal text-text-faint px-3 py-2">Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((row) => (
                          <tr key={row.month} className="border-b border-border last:border-0 hover:bg-bg-s2 transition-colors">
                            <td className="text-right tabular-nums text-text-muted px-3 py-1.5">{row.month}</td>
                            <td className="text-right tabular-nums text-text px-3 py-1.5">{fmt(row.payment, 2)}</td>
                            <td className="text-right tabular-nums text-success px-3 py-1.5">{fmt(row.principal, 2)}</td>
                            <td className="text-right tabular-nums text-warning px-3 py-1.5">{fmt(row.interest, 2)}</td>
                            <td className="text-right tabular-nums font-medium text-text px-3 py-1.5">{fmt(row.balance, 2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {schedule.length > 12 && (
                    <div className="px-4 py-2 border-t border-border text-center bg-bg-surface">
                      <button
                        type="button"
                        onClick={() => setShowAllRows((v) => !v)}
                        className="text-[11px] font-medium text-accent hover:text-accent-dark transition-colors"
                      >
                        {showAllRows
                          ? 'Show first 12 months'
                          : `Show all ${schedule.length} months`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── How to request ────────────────────────────────────────────────────── */}
      <div className="card p-5 space-y-4">
        <div>
          <h2 className="text-[15px] font-semibold text-text">How to Request</h2>
          <p className="text-sm text-text-muted mt-1 leading-relaxed">
            Use the AI assistant below — it runs all 12 FAP compliance checks automatically and
            routes your request through the supervised confirmation flow.
          </p>
        </div>

        <ul className="space-y-3">
          {[
            {
              label: 'Loan amount',
              hint: 'Max is lesser of $50,000 or 50% of vested balance',
              required: true,
            },
            {
              label: 'Repayment period',
              hint: '1–15 years (5 years max for general loans, 15 for primary home)',
              required: true,
            },
            {
              label: 'Purpose',
              hint: 'General, primary home purchase, etc.',
              required: false,
            },
          ].map((f) => (
            <li key={f.label} className="flex items-start gap-3">
              <div className={[
                'w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
                f.required ? 'bg-accent-light' : 'bg-bg-s3',
              ].join(' ')}>
                <span className={[
                  'text-[9px] font-bold',
                  f.required ? 'text-accent-dark' : 'text-text-faint',
                ].join(' ')}>
                  {f.required ? '!' : '?'}
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-text">{f.label}</span>
                {f.hint && (
                  <span className="text-xs text-text-muted ml-1.5">— {f.hint}</span>
                )}
                {!f.required && (
                  <span className="ml-1.5 text-[10px] font-mono text-text-faint bg-bg-s3 px-1.5 py-0.5 rounded">
                    optional
                  </span>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* ── Example queries + send to chat ───────────────────────────────────── */}
      <div className="card p-5 space-y-5">
        <h2 className="text-[15px] font-semibold text-text">Request a Loan</h2>

        {/* Example pills */}
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint mb-3">
            Example queries — click to use
          </p>
          <div className="space-y-2">
            {EXAMPLE_QUERIES.map((q) => {
              const isSelected = selectedQuery === q && customQuery === q;
              return (
                <button
                  key={q}
                  type="button"
                  onClick={() => selectExample(q)}
                  className={[
                    'w-full text-left text-sm px-4 py-3 rounded-lg border transition-colors duration-150',
                    isSelected
                      ? 'border-accent bg-accent-light text-accent-dark font-medium'
                      : 'border-border text-text-muted hover:border-accent/40 hover:bg-bg-s2 hover:text-text',
                  ].join(' ')}
                >
                  <span className="font-mono text-accent opacity-60 mr-1">"</span>
                  {q}
                  <span className="font-mono text-accent opacity-60 ml-0.5">"</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom textarea */}
        <div>
          <p className="text-[11px] font-mono uppercase tracking-widest text-text-faint mb-2">
            Or write your own
          </p>
          <textarea
            rows={2}
            value={customQuery}
            onChange={handleTextarea}
            placeholder='e.g. "I want to take a loan of $10,000 for 5 years"'
            className="w-full border border-border-strong rounded-xl px-4 py-3 text-sm text-text bg-bg
              outline-none resize-none focus:border-accent focus:ring-2 focus:ring-accent/10
              placeholder:text-text-faint leading-relaxed transition-all duration-150"
          />
        </div>

        <button
          type="button"
          onClick={sendToChat}
          disabled={!activeQuery || !!participant?.blackout_active}
          title={participant?.blackout_active ? 'Plan blackout in effect — requests disabled' : undefined}
          className="btn-primary flex items-center justify-center gap-2"
        >
          <SendIcon />
          {participant?.blackout_active ? 'Blackout in effect' : 'Send to chat'}
        </button>
      </div>

      <p className="text-[11px] text-text-faint text-center font-mono pb-2">
        All loan requests gate through FAP (12 ERISA rules) · IRC §72(p) · ERISA §408(b)(1)
      </p>
    </div>
  );
}
